import { FC, useEffect, useState, Dispatch, SetStateAction, useRef } from 'react'
import {
  Container
} from './styles'
import { RenewalData } from 'src/dtos/renewalData'
import ApplicationData  from 'src/dtos/applicationData'
import { api } from 'src/services/apiClient'
import { newApplicationNavigationRoutes } from 'src/constants/application'
import FirmSection from './FirmSection'
import RenewalFirmSection from './RenewalFirmSection'
import AttorneysSection from './AttorneysSection'
import RenewalAttorneysSection from './RenewalAttorneysSection'
import InsuranceSection from './InsuranceSection'
import QuotesSection from './QuotesSection'
import RenewalQuestions from './RenewalQuestions'
import JudgeBasicSection from './JudgeBasicSection'
import JudgeInsuranceSection from './JudgeInsuranceSection'
import JudgeRenewalQuestions from 'src/components/portal/default/AppListDrawerContent/JudgeRenewalQuestions'
import { FirmDetailsContactData } from 'src/dtos/portal'

interface AppListDrawerContentProps {
  applicationId: number;
  isNewBusiness: boolean;
  isJudge: boolean;
  application: RenewalData | undefined;
  setApplication: Dispatch<SetStateAction<RenewalData | undefined>>;
  primaryContactData : FirmDetailsContactData | undefined;
  setPrimaryContactData: Dispatch<SetStateAction<FirmDetailsContactData | undefined>>;
  appSummaryRef?: any;
}


interface OuterResponse {
  id?: number,
  status?: number,
  userId?: number,
  step?: string,
  firmId?: number, 
  data: RenewalData | ApplicationData
}

const AppListDrawerContent: FC<AppListDrawerContentProps> = ({ 
  applicationId, 
  isNewBusiness, 
  isJudge, 
  application, 
  setApplication,
  primaryContactData,
  setPrimaryContactData,
  appSummaryRef = null
}) => {

  const [loading, setLoading] = useState(false)
  const [sectionsCompleted, setSectionsCompleted] = useState<{ [key: string]: boolean }>({
    basic: false,
    firm: false,
    attorneys: false,
    insurance: false,
    quote: false,
    renewal: false
  })

  const isJudgeRenewal = isJudge && application?.judgeRenewalSection !== undefined

  useEffect(() => {
    const getApplicationData = async () => {
      setLoading(true)
      try {
        let outer: OuterResponse;
        let data: RenewalData | ApplicationData | undefined;
        if (isNewBusiness) {
          outer = await (await api.get(`Applications/${applicationId}`)).data; 
          data = outer.data
        } else {
          outer = await (await api.get(`Renewals/${applicationId}`)).data;          
          data = outer.data
        }
        setApplication(data)

        if(outer && data){
            let contactData : FirmDetailsContactData;
            contactData = await (await api.get(`Firm/GetApplicantUserPseudoContact?firmId=${outer.firmId}`)).data
            setPrimaryContactData(contactData)
        }

        if (data?.completionPercentage === 100) {
          setSectionsCompleted({
            basic: true,
            firm: true,
            attorneys: true,
            insurance: true,
            quote: true,
            renewal: true
          })
        } else {
          const routesArray = newApplicationNavigationRoutes.map(rt => rt.route)

          const currentRouteIndex = routesArray.findIndex(rt => data?.stoppedAt?.includes(`/${rt}/`))

          let updatedSections = { ...sectionsCompleted }

          for (const key of Object.keys(sectionsCompleted)) {
            if (currentRouteIndex > routesArray.indexOf(key)) {
              updatedSections[key] = true
            } else {
              updatedSections[key] = false
            }
          }
          setSectionsCompleted(updatedSections)
        }
      } catch (error) {

      } finally {
        setLoading(false)
      }
    }

    if (applicationId) {
      getApplicationData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, isNewBusiness, setApplication,setPrimaryContactData])

  const renderBasicSection = () => {
    if (isJudge) {
      return <JudgeBasicSection
        basicSection={application?.judgeApplicationData?.basicSection}
        isComplete={sectionsCompleted.basic}
        isRenewal={isJudgeRenewal}
        renewalBasicSection={application?.judgeRenewalSection?.basicSection} />
    }
    if (isNewBusiness) {
      return <FirmSection firmSection={application?.firmSection} isComplete={sectionsCompleted.firm} primaryContactData = {primaryContactData} />
    }
    return <RenewalFirmSection renewalData={application} isComplete={sectionsCompleted.attorneys} primaryContactData = {primaryContactData} />
  }

  const renderAttorneysSection = () => {
    if (isJudge) {
      return null
    }
    if (isNewBusiness) {
      return <AttorneysSection attorneysSection={application?.attorneysSection} isComplete={sectionsCompleted.attorneys} />
    }
    return <RenewalAttorneysSection renewalData={application} isComplete={sectionsCompleted.attorneys} />
  }

  const renderInsuranceSection = () => {
    if (isJudgeRenewal) {
      return null
    }
    if (isJudge) {
      return <JudgeInsuranceSection insuranceSection={application?.judgeApplicationData?.insuranceSection} isComplete={sectionsCompleted.insurance} />
    }
    if (isNewBusiness) {
      return <InsuranceSection insuranceSection={application?.liabilitySection} isComplete={sectionsCompleted.insurance} />
    }
    return null
  }

  const renderRenewalQuestions = () => {
    if (isJudgeRenewal) {
      return <JudgeRenewalQuestions questions={application.judgeRenewalSection?.questions} isComplete={sectionsCompleted.renewal} />
    }
    if (!isNewBusiness) {
      return <RenewalQuestions renewalData={application} isComplete={sectionsCompleted.renewal} />
    }
    return null
  }

  const renderQuotesSection = () => {
    if (isJudge) {
      return <QuotesSection quotesSection={application?.judgeApplicationData?.quoteData?.quotes} isComplete={sectionsCompleted.quote} />
    }
    return <QuotesSection quotesSection={application?.quotesSection?.quotes} isComplete={sectionsCompleted.quote} />
  }

  return (
    <Container id="app-list-drawer-content" ref={appSummaryRef}>
      {loading ? <span>Loading...</span> : (
        <>
          {renderBasicSection()}
          {renderAttorneysSection()}
          {renderInsuranceSection()}
          {renderRenewalQuestions()}
          {renderQuotesSection()}
        </>
      )}
    </Container>
  )
}

export default AppListDrawerContent